import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import type {
  TagAutocompleteHandle,
  TagAutocompleteProps,
} from './TagAutocomplete.types'
import { tagColorBorder, tagColorToHex } from './tagging.utils'

export const TagAutocomplete = forwardRef<
  TagAutocompleteHandle,
  TagAutocompleteProps
>((props, ref) => {
  const { onFocusInput, onSelect, orgTags, query } = props
  const [dismissed, setDismissed] = useState(false)
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])

  const suggestions = useMemo(() => {
    if (dismissed) {
      return []
    }

    const trimmed = query.trim().toLowerCase()

    if (!trimmed) {
      return [...orgTags]
        .sort((a, b) => Number(b.id) - Number(a.id))
        .slice(0, 3)
    }

    return orgTags
      .filter(tag => tag.name.toLowerCase().includes(trimmed))
      .sort((a, b) => {
        const aPrefix = a.name.toLowerCase().startsWith(trimmed) ? 0 : 1
        const bPrefix = b.name.toLowerCase().startsWith(trimmed) ? 0 : 1

        return aPrefix - bPrefix
      })
      .slice(0, 5)
  }, [query, orgTags, dismissed])

  useEffect(() => {
    setDismissed(false)
  }, [query, orgTags])

  useEffect(() => {
    buttonRefs.current.length = suggestions.length
  }, [suggestions.length])

  const handleButtonKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()

        const next = index >= suggestions.length - 1 ? 0 : index + 1

        buttonRefs.current[next]?.focus()

        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()

        if (index === 0) {
          onFocusInput()
        } else {
          buttonRefs.current[index - 1]?.focus()
        }

        return
      }

      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        e.stopPropagation()
        onSelect(suggestions[index])
        onFocusInput()

        return
      }

      if (e.key === 'Escape') {
        if (query.trim()) {
          e.stopPropagation()
          setDismissed(true)
        }

        onFocusInput()
      }
    },
    [suggestions, onSelect, onFocusInput, query],
  )

  useImperativeHandle(
    ref,
    () => ({
      clearSuggestions: () => setDismissed(true),
      focusFirst: () => {
        buttonRefs.current[0]?.focus()
      },
      handleKeyDown: (e: KeyboardEvent) => {
        if (suggestions.length === 0) {
          return false
        }

        if (e.key === 'Tab') {
          e.preventDefault()
          onSelect(suggestions[0])

          return true
        }

        return false
      },
      hasSuggestions: () => suggestions.length > 0,
    }),
    [suggestions, onSelect],
  )

  if (suggestions.length === 0) {
    return null
  }

  return (
    <div className="TagAutocomplete">
      {suggestions.map((tag, index) => (
        <button
          key={tag.id}
          ref={el => {
            buttonRefs.current[index] = el
          }}
          className="TagPill TagAutocomplete-item"
          onClick={() => {
            onSelect(tag)
            onFocusInput()
          }}
          onKeyDown={e => handleButtonKeyDown(e, index)}
          style={{
            backgroundColor: tagColorToHex(tag.color),
            borderColor: tagColorBorder(tag.color),
          }}
          type="button"
        >
          {tag.name}
        </button>
      ))}
    </div>
  )
})

TagAutocomplete.displayName = 'TagAutocomplete'
